import { Injectable } from '@angular/core';
import _ from 'lodash';
import { ISearchResourceConfig } from '../../resource-dictionary/search-resource-dictionary-types';
import { DatePipe } from '@angular/common';
import { ISearchFilter } from './search-query-types.service';

export interface ISearchQueryService {
    buildQueryByIdString(resourceId: string): string;
    buildQueryString(baseQuery?: string, rawTextQuery?: string, msearchFilters?: any[], resourceConfig?: ISearchResourceConfig): string;
    restrictQueryToNameField(rawQuery: string, resourceConfig: ISearchResourceConfig): string;
    excludeHiddenFiles(): string;
}

/**
 * Manages search query formatting necessary to interact with `v2/search` bssh api
 * Angular 1 Code base reference: Tang.Modules/Directives/Search/searchQuery.service.ts
 */
@Injectable({
    providedIn: 'root'
})
export class SearchQueryService implements ISearchQueryService {

    // Not all methods are used currently. Ported over the service as is from the Angular 1 code base.
    // Will be used once we add more search functionality on the A8 Site.
    private static andDelimiter = ' AND ';
    private static orDelimiter = ' OR ';
    // private static assigner = ":";
    private searchOperatorRegex: RegExp = /\*|!|:|\sand\s|\sor\s|\snot\s|~|\^|\?|\\|\||\(|\[|\{|\"/i;

    constructor(private datePipe: DatePipe) {
    }

    /**
     * Return a query string that looks for an entity with the specified ID.
     */
    public buildQueryByIdString(resourceId: string): string {
        return '(id:' + resourceId + ')';
    }

    /**
     * Builds a Elastic search specific query string that can be passed in the `v2/search` api request params
     * @param baseQuery The base query for the resource
     * @param rawTextQuery Raw text query
     * @param searchFilters Search Filters
     * @param resourceConfig Config
     */
    public buildQueryString(
        baseQuery: string = '',
        rawTextQuery: string = '',
        searchFilters: ISearchFilter[] = [],
        resourceConfig?: ISearchResourceConfig
    ): string {
        let finalQueryString;
        let structuredQueryString = '';

        // Escape colons that appear in timestamp patterns (HH:MM:SS) to prevent 500 internal error for Search API
        rawTextQuery = rawTextQuery.replace(/(\d{1,2}):(\d{1,2}):(\d{1,2})/g, '$1\\:$2\\:$3');

        const isAlreadyStructured = rawTextQuery.search(this.searchOperatorRegex) > -1;

        if (isAlreadyStructured) {
            finalQueryString = rawTextQuery;
        } else {
            rawTextQuery = rawTextQuery.trim();
            // When there is no free-text search we intentionally emit an empty match-all term rather than a bare `*`.
            // A bare wildcard forces OpenSearch to field-expand across every mapped field, which exceeds the 1024
            // field-expansion limit on large indices and 500s (BASE-89131). An empty term is dropped from the
            // concatenation below so the search API receives an empty query and returns everything server-side.
            if (resourceConfig) {
                finalQueryString = rawTextQuery ? this.restrictQueryToNameField(rawTextQuery, resourceConfig) : '';
            } else {
                finalQueryString = rawTextQuery ? rawTextQuery : '';
            }
        }

        // tslint:disable-next-line: prefer-for-of
        for (let i = 0; i < searchFilters.length; i++) {
            const filter: ISearchFilter = searchFilters[i];
            const queryParam = this.buildQueryForFacets(filter);
            if (structuredQueryString) {
                structuredQueryString = [structuredQueryString, queryParam].join(SearchQueryService.andDelimiter);
            } else {
                structuredQueryString = queryParam;
            }
        }

        if (structuredQueryString) {
            finalQueryString = this.andWrap(finalQueryString, structuredQueryString);
        }

        if (baseQuery) {
            finalQueryString = this.andWrap(finalQueryString, baseQuery);
        }

        if (resourceConfig && resourceConfig.baseQuery) {
            finalQueryString = this.andWrap(finalQueryString, resourceConfig.baseQuery);
        }
        return finalQueryString;
    }

    /**
     * ANDs `clause` onto `existing`, wrapping each operand in a single pair of parentheses for grouping.
     * If an operand is already a single balanced parenthesized group (e.g. `(Project.Id:1)`), it is not
     * re-wrapped — this avoids the redundant `((...))` nesting that appeared when a match-all base query
     * (already wrapped once) was fed into a second base-query concatenation.
     */
    private andWrap(existing: string, clause: string): string {
        const wrapped = this.groupIfNeeded(clause);
        if (!existing) {
            return wrapped;
        }
        return this.groupIfNeeded(existing) + SearchQueryService.andDelimiter + wrapped;
    }

    /**
     * Wraps `query` in a single pair of parentheses, unless it is already wrapped in one.
     *
     * A query is "already wrapped" only when its outermost parentheses span the ENTIRE string —
     * e.g. `(a AND b)`. That is NOT the same as merely starting with `(` and ending with `)`:
     * `(a) AND (b)` also does so, yet its outer parens belong to two separate groups and it still
     * needs wrapping to bind correctly when AND-ed with another clause.
     *
     * The test is simple: strip one layer of outer parens and check whether what remains is still
     * paren-balanced. If it is, the stripped parens were a genuine single wrapper (so we keep the
     * original and avoid redundant `((x))` nesting). If not (balance goes negative partway), the
     * outer parens closed early and belonged to separate sub-groups, so we wrap.
     */
    private groupIfNeeded(query: string): string {
        // The last check is the important one. `(a) AND (b)` also starts with '(' and ends with ')',
        // but those belong to two SEPARATE groups — stripping them leaves `a) AND (b`, which is NOT
        // balanced, so we correctly wrap it. Only a genuine single wrapper like `(a AND b)` leaves a
        // balanced remainder (`a AND b`) and is kept as-is to avoid redundant `((...))` nesting.
        const alreadyWrapped =
            query.charAt(0) === '(' &&
            query.charAt(query.length - 1) === ')' &&
            this.isBalanced(query.slice(1, -1));
        return alreadyWrapped ? query : '(' + query + ')';
    }

    /**
     * True if every '(' is matched by a later ')' and the string never has more ')' than '(' at any
     * point — i.e. the parentheses are well-formed with no unbalanced tail.
     */
    private isBalanced(query: string): boolean {
        let depth = 0;
        for (const ch of query) {
            if (ch === '(') {
                depth++;
            } else if (ch === ')' && --depth < 0) {
                return false;
            }
        }
        return depth === 0;
    }

    public excludeHiddenFiles(): string {
        const filteredDate = this.datePipe.transform(this.getTime(), 'yyyy-MM-ddTHH:mm:ss');
        return `+AND+-(path:%27indexes//data*%27)\
              +AND+-(path:%27coverage//data*%27)\
              +AND+datemodified:[*+TO+${filteredDate}]\
              +AND+filestatus:UploadComplete\
              +AND+size:[1+TO+*]`;
    }

    private buildQueryForFacets(filter: ISearchFilter): string {
        const queryBits = _.map(filter.facets, (facet) => {
            if (facet === 'fileextension') {
                const extensions = Array.isArray(filter.currentChoice.value)
                    ? filter.currentChoice.value as string[]
                    : [filter.currentChoice.value as string];
                // `strict` is deprecated and ignored (see buildFileExtensionQuery); every extension filter
                // is now an exact-match clause. Passed through only until IFilterChoice.strict is removed.
                const extensionQueries = extensions.map(ext => this.buildFileExtensionQuery(ext, filter.currentChoice.strict));
                return extensionQueries.length === 1 ? extensionQueries[0] : '(' + extensionQueries.join(' OR ') + ')';
            }

            return `(${facet}:${filter.currentChoice.value})`;
        });
        return queryBits.length === 1 ? queryBits[0] : '(' + queryBits.join(' OR ') + ')';
    }

    // Build a filter clause against the backend `fileextensions` keyword field (BASE-89259), which stores
    // every progressive dot-suffix of a file name (e.g. `sample.fastq.gz` -> [".gz", ".fastq.gz"]). The
    // search service rewrites each `fileextensions:"V"` clause we emit into
    //   (fileextensions.parts:"V" OR fileextensions:".V"^10)
    // where `fileextensions.parts` tokenizes each stored suffix on '.'. So `fileextensions:"fastq"` already
    // matches ANY extension containing the `fastq` part (`.fastq`, `.fastq.gz`, `.fastq.bai`, ...), and the
    // `^10` boosts the exact whole-extension match (`.fastq`). A leading dot on the value is normalized away
    // server-side, so we send it without one.
    //
    // NOTE ON `strict` (deprecated, always treated as true): this used to toggle a "non-strict" expansion
    // that OR-ed in the `.gz` sibling clause, e.g. `.fastq` -> (fileextensions:"fastq" OR fileextensions:"fastq.gz").
    // That expansion turned out to be pointless because of how `.parts` tokenizes: `fileextensions:"fastq"`
    // ALREADY returns every `.fastq.gz` file (they carry the `fastq` part), so the extra `.gz` clause added
    // ZERO documents. Worse, it handed `.fastq.gz` files their own `^10` exact boost (on `.fastq.gz`) plus an
    // extra part match, so it actually ranked `.fastq.gz` at or ABOVE the plain `.fastq` file the user searched
    // for, undermining the exact-match boost. The only case it changed the result set was a query whose value
    // itself ended in `.gz` (non-strict `.fastq.gz` would also pull in bare `.fastq`), which is not desired.
    //
    // So there is no longer a meaningful non-strict mode. This method now always emits the single exact clause
    // (equivalent to the old strict path) regardless of the flag. The `strict` parameter is retained only so
    // callers and `IFileExtensionFilter` / `IFilterChoice.strict` do not have to change; it is a no-op and can
    // be removed once those types are cleaned up. See BASE-89260.
    // tslint:disable-next-line: no-unused-variable
    private buildFileExtensionQuery(ext: string, _strict?: boolean): string {
        const value = ext.replace(/^\./, '');
        return `(fileextensions:"${value}")`;
    }

    private toUTCDate(date: Date) {
        // tslint:disable-next-line:no-unnecessary-local-variable
        const utc = new Date(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate(),
            date.getUTCHours(),
            date.getUTCMinutes(),
            date.getUTCSeconds()
        );
        return utc;
    }

    private millisToUTCDate(millis: number) {
        return this.toUTCDate(new Date(millis));
    }

    private getTime() {
        const date = new Date();
        return this.millisToUTCDate(date.getTime()).getTime();
    }

    public restrictQueryToNameField(rawQuery: string, resourceConfig: ISearchResourceConfig): string {
        if (resourceConfig.searchObjectName && resourceConfig.searchProperties && resourceConfig.searchProperties.length === 1) {
            // Build a query like Project.Name:"ABC"
            const prefix = resourceConfig.searchObjectName + '.' + resourceConfig.searchProperties[0] + ':';
            return prefix + '"' + rawQuery + '"';
        } else if (resourceConfig.searchObjectName && resourceConfig.searchProperties) {
            // Build a query like (Run.ExperimentName:"ABC" OR Run.FlowcellBarcode:"ABC")
            const propertyQueries = _.map(resourceConfig.searchProperties, (key) => {
                return `${resourceConfig.searchObjectName}.${key}:"${rawQuery}"`;
            });
            return '(' + propertyQueries.join(' OR ') + ')';
        } else {
            return rawQuery;
        }
    }
}
